import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '../format';
import { useMcpAudit } from '../hooks/use-mcp';
import { TableStateRow } from './table-state-row';
import { HttpStatusBadge } from './token-badges';

const PAGE_SIZE = 50;

interface AuditTableProps {
  /** true — журнал всех администраторов (mcp.manage). */
  all: boolean;
}

/** Журнал вызовов MCP-инструментов с серверной пагинацией. */
export function AuditTable({ all }: AuditTableProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error } = useMcpAudit(all, page, PAGE_SIZE);
  const items = data?.items ?? [];
  const totalPages = Math.max(1, data?.meta.totalPages ?? 1);
  const showRows = !isLoading && !error && items.length > 0;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Время</TableHead>
              {all ? <TableHead>Администратор</TableHead> : null}
              <TableHead>Инструмент</TableHead>
              <TableHead>Метод</TableHead>
              <TableHead>Путь</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Длительность</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!showRows ? (
              <TableStateRow colSpan={all ? 7 : 6} isLoading={isLoading} error={error} emptyText="Вызовов пока не было" />
            ) : null}
            {showRows &&
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="whitespace-nowrap text-sm">{formatDateTime(item.createdAt)}</TableCell>
                  {all ? <TableCell className="text-sm">{item.adminEmail}</TableCell> : null}
                  <TableCell className="font-mono text-xs">{item.toolName}</TableCell>
                  <TableCell className="font-mono text-xs">{item.method}</TableCell>
                  <TableCell className="max-w-xs truncate font-mono text-xs" title={item.path}>
                    {item.path}
                  </TableCell>
                  <TableCell>
                    <HttpStatusBadge status={item.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm">{item.durationMs} мс</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            Страница {page} из {totalPages} · всего {data?.meta.total ?? 0}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage((current) => current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
